import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, BookOpen, Tag, Brain, Microscope, FlaskConical, Sparkles, Dna, Bug, Clock } from 'lucide-react';

export const KnowledgeBase = () => {
  const { API, token } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  // Condition quick-access buttons
  const conditionButtons = [
    { slug: 'pandas-pans', label: 'PANDAS/PANS', icon: Brain, color: 'bg-primary hover:bg-primary/90 text-white' },
    { slug: 'mycoplasma-pneumonia', label: 'Mycoplasma', icon: Microscope, color: 'bg-accent hover:bg-accent/90 text-white' },
    { slug: 'histamine-intolerance', label: 'Histamine', icon: FlaskConical, color: 'bg-secondary hover:bg-secondary/80 text-secondary-foreground' },
    { slug: 'mast-cell-activation', label: 'Mast Cell', icon: Sparkles, color: 'bg-purple-500 hover:bg-purple-600 text-white' },
    { slug: 'mthfr-gene', label: 'MTHFR Gene', icon: Dna, color: 'bg-blue-500 hover:bg-blue-600 text-white' },
    { slug: 'mold', label: 'Mold', icon: Bug, color: 'bg-stone-500 hover:bg-stone-600 text-white' },
    { slug: 'co-infections', label: 'Co-Infections', icon: Microscope, color: 'bg-rose-500 hover:bg-rose-600 text-white' },
    { slug: 'parasites', label: 'Parasites', icon: Bug, color: 'bg-amber-600 hover:bg-amber-700 text-white' },
  ];

  const categories = [
    { value: 'all', label: 'All Topics', color: 'bg-stone-200 text-stone-700' },
    { value: 'PANDAS/PANS', label: 'PANDAS/PANS', color: 'bg-primary/20 text-primary' },
    { value: 'Mycoplasma Pneumonia', label: 'Mycoplasma Pneumonia', color: 'bg-accent/20 text-accent' },
    { value: 'Histamine Intolerance', label: 'Histamine Intolerance', color: 'bg-secondary text-secondary-foreground' },
    { value: 'Mast Cell Activation', label: 'Mast Cell Activation', color: 'bg-primary/30 text-primary' },
  ];

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    filterArticles();
  }, [articles, searchQuery, selectedCategory]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/articles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterArticles = () => {
    let filtered = articles;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(query) ||
        article.excerpt.toLowerCase().includes(query) ||
        article.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    setFilteredArticles(filtered);
  };

  return (
    <div className="space-y-8" data-testid="knowledge-base">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-8 md:p-12">
        <div className="flex items-center gap-4 mb-4">
          <BookOpen className="w-12 h-12 text-primary" strokeWidth={1.5} />
          <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
            Knowledge Base
          </h1>
        </div>
        <p className="text-lg text-[#57534E] leading-relaxed mb-6">
          Comprehensive medical education on PANDAS/PANS and related conditions with in-depth guides, treatment protocols, and practical strategies.
        </p>

        {/* Condition Quick Access Buttons */}
        <div className="flex flex-wrap gap-3">
          {conditionButtons.map((btn) => {
            const Icon = btn.icon;
            return (
              <div key={btn.slug} className="relative">
                <button
                  onClick={() => !btn.comingSoon && navigate(`/condition/${btn.slug}`)}
                  className={`${btn.color} px-4 py-2.5 rounded-full font-semibold text-sm flex items-center gap-2 transition-all duration-300 shadow-sm ${btn.comingSoon ? 'cursor-not-allowed opacity-75' : 'hover:shadow-md'}`}
                  data-testid={`condition-btn-${btn.slug}`}
                  disabled={btn.comingSoon}
                >
                  <Icon className="w-4 h-4" />
                  {btn.label}
                </button>
                {btn.comingSoon && (
                  <span className="absolute -top-2 -right-2 bg-amber-400 text-amber-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm flex items-center gap-0.5">
                    <Clock className="w-2.5 h-2.5" />
                    Soon
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Search and Filter */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
          <Input
            type="text"
            placeholder="Search articles, symptoms, treatments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 bg-white border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-lg h-14 text-base"
            data-testid="search-input"
          />
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-3">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                selectedCategory === category.value
                  ? category.color + ' shadow-md'
                  : 'bg-white text-[#57534E] border border-stone-200 hover:border-primary'
              }`}
              data-testid={`category-${category.value}`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Articles Grid */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-[#A8A29E]">Loading articles...</p>
        </div>
      ) : filteredArticles.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
          <p className="text-[#57534E] text-lg">No articles found matching your criteria.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredArticles.map((article) => (
            <Card
              key={article.id}
              className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 hover:shadow-[0_8px_24px_rgba(124,154,146,0.15)] hover:border-primary/30 transition-all duration-300 cursor-pointer hover:-translate-y-1"
              onClick={() => navigate(`/knowledge-base/${article.id}`)}
              data-testid={`article-card-${article.id}`}
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-xl font-nunito font-bold text-[#2D2D2D] leading-tight flex-1">
                    {article.title}
                  </h3>
                  <BookOpen className="w-6 h-6 text-primary flex-shrink-0" strokeWidth={1.5} />
                </div>

                <p className="text-[#57534E] leading-relaxed line-clamp-3">
                  {article.excerpt}
                </p>

                <div className="flex flex-wrap gap-2">
                  {article.tags.slice(0, 4).map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium flex items-center gap-1"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    article.category === 'PANDAS/PANS' ? 'bg-primary/20 text-primary' :
                    article.category === 'Mycoplasma Pneumonia' ? 'bg-accent/20 text-accent' :
                    article.category === 'Histamine Intolerance' ? 'bg-secondary text-secondary-foreground' :
                    'bg-primary/30 text-primary'
                  }`}>
                    {article.category}
                  </span>
                  <Button
                    className="text-primary hover:text-primary/80 font-semibold"
                    variant="ghost"
                    data-testid={`read-article-${article.id}`}
                  >
                    Read More →
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase;
